<template>
  <Teleport to="#app">
    <div class="content-subpage-container" :class="{ 'is-visible': isVisible }">
      <component
        :is="subpage.component"
        v-for="subpage in subpages"
        :key="subpage.id"
        ref="subpagesRef"
        :params="subpage.params"
        v-on="subpage.listeners"
      />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import DOMUtils from "@utils/dom.utils.ts";
import RandomUtils from "@utils/random.utils";
import { useStore } from "vuex";
import contentSubpageEventBus from "@plugins/content-subpage/service/event-bus.ts";
import { isEmpty } from "@utils/var.check";
import { ref, computed, watch, onMounted, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { useEventBusWatch } from "@plugins/common/composables/event-bus-watch.ts";
import { useVm } from "@utils/vm.ts";

// Composable
const { vmExpose } = useVm();
const { t } = useI18n();
const { trackWatch } = useEventBusWatch();
const store = useStore();

// Data
const subpages = ref([]);
const hideSubpages = ref(false);
const hideSubpageMarker = ref([]);

// Template refs
const subpagesRef = useTemplateRef<HTMLElement>("subpagesRef");

// Computed
const isVisible = computed(() => {
  if (hideSubpages.value) {
    return false;
  }
  return subpages.value.length > 0;
});

// Methods
const addContentSubpage = (id: string) => {
  store.dispatch("app/addContentSubpage", id);
};

const removeContentSubpage = (id: string) => {
  store.dispatch("app/removeContentSubpage", id);
};

const add = (component, params = {}, listeners = {}) => {
  //check if there is existing subpage
  const currentContentSubpage = subpages.value[0];
  const componentName = component.name ?? component.__name;
  const currentContentSubpageName = isEmpty(currentContentSubpage)
    ? ""
    : (currentContentSubpage.name ?? currentContentSubpage.__name);
  const isSubpagesHavingSameComponentName = componentName === currentContentSubpageName;

  if (subpages.value.length > 0 && isSubpagesHavingSameComponentName) {
    return;
  }

  let id;
  let contextId;
  const PREFIX = "contentSubpage_";

  if (params.customContextId) {
    id = PREFIX + params.customContextId;
    contextId = id;
  } else {
    id = PREFIX + RandomUtils.randomId();
    contextId = params.hasParentSubpage ? subpages.value[subpages.value.length - 1].contextId : id;
  }

  params.id = id;
  params.contextId = contextId;

  subpages.value.push({
    id,
    component,
    params,
    contextId,
    listeners,
  });
  addContentSubpage(id);
  hideSubpages.value = false;
  return id;
};

const remove = (id) => {
  const index = subpages.value.findIndex((subpage) => subpage.id === id);
  if (index === -1) {
    return;
  }
  const subpage = subpagesRef.value?.[index];
  subpage.promptComponentDirty().then((response) => {
    if (!response) {
      return;
    }
    subpage.actionBackCallBack();
    removeContentSubpage(id);
    subpages.value.splice(index, 1);
  });
};

const promptCurrentContextSubpageDirty = () => {
  return new Promise((resolve) => {
    if (!subpagesRef.value) {
      return resolve(true);
    }
    const currentContext = subpages.value[subpages.value.length - 1].contextId;
    const contextSubpages = subpagesRef.value.filter((subpage) => {
      return currentContext === subpage.params.contextId;
    });
    const hasDirtySubpage = contextSubpages.find((subpage) => {
      return subpage.isComponentDirty();
    });
    if (!hasDirtySubpage) {
      if (contextSubpages.length > 0) {
        contextSubpages.forEach((subpage) => {
          subpage.removeComponentDirty();
          setTimeout(() => {
            subpage.actionHiddenCallBack();
          }, 200);
        });
      }
      return resolve(true);
    }
    if (!confirm(t("dirty_confirmation_prompt"))) {
      return resolve(false);
    } else {
      contextSubpages.forEach((subpage) => {
        subpage.removeComponentDirty();
        setTimeout(() => {
          subpage.actionHiddenCallBack();
        }, 200);
      });
      return resolve(true);
    }
  });
};

const promptSubpageDirty = () => {
  return new Promise((resolve) => {
    const hasDirtySubpage = !subpagesRef.value
      ? false
      : subpagesRef.value.find((subpage) => {
          return subpage.isComponentDirty();
        });
    if (!hasDirtySubpage) {
      if (subpagesRef.value && subpagesRef.value.length > 0) {
        subpagesRef.value?.[subpagesRef.value.length - 1].actionHiddenCallBack();
      }
      return resolve(true);
    }
    if (!confirm(t("dirty_confirmation_prompt"))) {
      return resolve(false);
    } else {
      subpagesRef.value.forEach((subpage) => {
        subpage.removeComponentDirty();
        setTimeout(() => {
          subpage.actionHiddenCallBack();
        }, 200);
      });
      return resolve(true);
    }
  });
};

const removeCurrentContextSubpages = () => {
  promptCurrentContextSubpageDirty().then((response) => {
    if (!response) {
      return;
    }
    const currentContext = subpages.value[subpages.value.length - 1].contextId;
    subpages.value = subpages.value.filter((subpage) => {
      if (subpage.contextId === currentContext) {
        removeContentSubpage(subpage.id);
        return false;
      }
      return true;
    });
  });
};

const removeAll = () => {
  promptSubpageDirty().then((response) => {
    if (!response) {
      return;
    }
    subpages.value.forEach((subpage) => {
      removeContentSubpage(subpage.id);
    });
    subpages.value = [];
  });
};

const hasOpenedContentSubpage = () => {
  if (subpages.value && subpages.value.length > 0) {
    remove(subpages.value[subpages.value.length - 1].id);
    return true;
  } else {
    return false;
  }
};

const getCurrentBreadcrumb = () => {
  if (subpagesRef.value && subpagesRef.value.length > 0) {
    return subpagesRef.value?.[subpagesRef.value.length - 1].titles;
  }
  return [];
};

const forceHideSubPages = () => {
  contentSubpageEventBus.$emit("content-subpage-force-hide", null);
};

const showHiddenSubpages = () => {
  contentSubpageEventBus.$emit("content-subpage-force-show", null);
};

// Watch
watch(
  subpages,
  (subpagesValue) => {
    const noOfSubpage = subpagesValue.length;
    const lastSubPageId = subpagesValue[subpagesValue.length - 1]?.id;

    const subpagesHasMarker = subpagesValue
      .map((subpage) => subpage.id)
      .filter((id) => hideSubpageMarker.value[hideSubpageMarker.value.length - 1] === id);

    const originalSubpages = subpagesRef.value;
    if (originalSubpages) {
      originalSubpages.forEach((subpage, index) => {
        if (noOfSubpage - 1 === index) {
          // Continue to hide all subpages if the last subpage has force hidden triggered
          if (hideSubpageMarker.value.indexOf(lastSubPageId) !== -1) {
            subpage.setVisible(false);
            hideSubpages.value = true;
          } else {
            // Remove subpage marker since it has been removed
            if (!subpagesHasMarker) {
              hideSubpageMarker.value.pop();
            }

            //last subpage is the active subpage
            subpage.setVisible(true);
          }
        } else {
          subpage.setVisible(false);
        }
      });
    }

    //to prevent scrolling when subpage is open
    if (hideSubpages.value || noOfSubpage === 0) {
      DOMUtils.removeClass(document.documentElement, "content-subpage-open");
    } else if (!hideSubpages.value && noOfSubpage > 0) {
      DOMUtils.addClass(document.documentElement, "content-subpage-open");
    }
  },
  { deep: true },
);

watch(hideSubpages, (value) => {
  if (!value) {
    const noOfSubpage = subpages.value.length;
    if (subpagesRef.value) {
      subpagesRef.value.forEach((subpage, index) => {
        if (noOfSubpage - 1 === index) {
          //last subpage is the active subpage
          subpage.setVisible(true);
        } else {
          subpage.setVisible(false);
        }
      });
    }

    //to prevent scrolling when subpage is open
    if (noOfSubpage === 0) {
      DOMUtils.removeClass(document.documentElement, "content-subpage-open");
    } else {
      DOMUtils.addClass(document.documentElement, "content-subpage-open");
    }
  } else {
    DOMUtils.removeClass(document.documentElement, "content-subpage-open");
  }
});

// Mounted
onMounted(() => {
  trackWatch(
    contentSubpageEventBus.$on("content-subpage-hidden", () => {
      removeCurrentContextSubpages();
    }),
  );

  trackWatch(
    contentSubpageEventBus.$on("content-subpage-back", (id) => {
      remove(id);
    }),
  );

  trackWatch(
    contentSubpageEventBus.$on("content-subpage-force-hide", () => {
      // Add the page that triggered force hiding to a marker stack,
      // this will be used to control if subpages should be visible
      // when new subpages get added / removed after this event
      const lastSubpage = subpages.value[subpages.value.length - 1];
      if (lastSubpage && hideSubpageMarker.value.indexOf(lastSubpage.id) === -1) {
        hideSubpageMarker.value.push(lastSubpage.id);
      }
      hideSubpages.value = true;
    }),
  );

  trackWatch(
    contentSubpageEventBus.$on("content-subpage-force-show", () => {
      hideSubpageMarker.value = [];
      hideSubpages.value = false;
    }),
  );
});

vmExpose({
  add,
  remove,
  removeAll,
  hasOpenedContentSubpage,
  getCurrentBreadcrumb,
  forceHideSubPages,
  showHiddenSubpages,
});

defineExpose({
  add,
  remove,
  removeAll,
  hasOpenedContentSubpage,
  getCurrentBreadcrumb,
  forceHideSubPages,
  showHiddenSubpages,
});
</script>

<style lang="scss">
@import "@styles/layout/z-map";

.content-subpage-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: calc((var(--vh, 1vh) * 100));
  z-index: calc(#{z-map("top-header")} + 1);
  background-color: transparent;
  opacity: 0;
  visibility: hidden;

  body.rich-text-modal-open & {
    z-index: z-map("rich-text-modal-nested-component");
  }
}

.content-subpage-container.is-visible {
  opacity: 1;
  visibility: visible;
}
</style>
